import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * GameOverlay — OBS Browser Source overlay for live game scoreboard
 * 
 * URL: /overlay/:gameId
 * 
 * Query params:
 *   theme    = dark (default) | light | glass | minimal
 *   position = top-left | top-right | bottom-left | bottom-right | top-center | bottom-center (default)
 *   size     = sm | md (default) | lg
 *   clock    = 1 (default) | 0  (show/hide clock)
 *   names    = full (default) | abbr (3-letter abbreviation)
 *   animate  = 1 (default) | 0  (score change animation)
 */
export default function GameOverlay() {
  const { gameId } = useParams();
  const [searchParams] = useSearchParams();

  const theme = searchParams.get('theme') || 'dark';
  const position = searchParams.get('position') || 'top-center';
  const size = searchParams.get('size') || 'md';
  const showClock = searchParams.get('clock') !== '0';
  const nameMode = searchParams.get('names') || 'full';
  const animate = searchParams.get('animate') !== '0';

  const [game, setGame] = useState(null);
  const [connected, setConnected] = useState(false);
  const [scoreFlash, setScoreFlash] = useState({ home: false, away: false });
  const prevScore = useRef({ home: 0, away: 0 });
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  // Make body transparent for OBS browser source
  useEffect(() => {
    const prevBg = document.body.style.background;
    document.body.style.background = 'transparent';
    document.documentElement.style.background = 'transparent';
    return () => {
      document.body.style.background = prevBg;
      document.documentElement.style.background = '';
    };
  }, []);

  // Fetch initial game data
  useEffect(() => {
    const fetchGame = async () => {
      try {
        const res = await fetch(`${API_URL}/api/engine/games/${gameId}`);
        if (res.ok) {
          const data = await res.json();
          setGame(data);
          prevScore.current = { home: data.score?.home || 0, away: data.score?.away || 0 };
        }
      } catch (e) {
        console.error('Failed to fetch game:', e);
      }
    };
    fetchGame();
  }, [gameId]);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    let wsUrl;
    if (API_URL.startsWith('https://')) {
      wsUrl = `wss://${API_URL.replace('https://', '')}/api/engine/ws/game/${gameId}`;
    } else {
      wsUrl = `ws://${API_URL.replace('http://', '')}/api/engine/ws/game/${gameId}`;
    }

    try {
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        reconnectTimer.current = setTimeout(connectWebSocket, 3000);
      };
      ws.onerror = () => ws.close();

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.type === 'game_update' || msg.type === 'clock_update' || msg.type === 'score_update') {
            setGame(prev => prev ? { ...prev, ...msg } : prev);
          }
          if (msg.type === 'status' && msg.status) {
            setGame(prev => prev ? { ...prev, status: msg.status } : prev);
          }
          if ((msg.type === 'stat' || msg.type === 'undo') && msg.score) {
            setGame(prev => prev ? { ...prev, score: msg.score } : prev);
          }
          if (msg.type === 'timeout' && msg.clock) {
            setGame(prev => prev ? { ...prev, clock: msg.clock } : prev);
          }
        } catch (e) {
          // ignore parse errors
        }
      };

      wsRef.current = ws;
    } catch (e) {
      console.error('WS error:', e);
    }
  }, [gameId]);

  useEffect(() => {
    connectWebSocket();
    return () => {
      clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connectWebSocket]);

  // Score flash animation
  useEffect(() => {
    if (!game || !animate) return;
    const curr = { home: game.score?.home || 0, away: game.score?.away || 0 };
    if (curr.home !== prevScore.current.home) {
      setScoreFlash(f => ({ ...f, home: true }));
      setTimeout(() => setScoreFlash(f => ({ ...f, home: false })), 800);
    }
    if (curr.away !== prevScore.current.away) {
      setScoreFlash(f => ({ ...f, away: true }));
      setTimeout(() => setScoreFlash(f => ({ ...f, away: false })), 800);
    }
    prevScore.current = curr;
  }, [game?.score?.home, game?.score?.away, animate]);

  // Helpers
  const formatTime = (ms) => {
    if (!ms && ms !== 0) return '--:--';
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const getPeriodLabel = (period, sport = 'basketball') => {
    if (!period) return '1st';
    if (sport === 'soccer') {
      if (period === 1) return '1H';
      if (period === 2) return '2H';
      return `OT${period - 2}`;
    }
    const labels = ['1st', '2nd', '3rd', '4th'];
    if (period <= 4) return labels[period - 1];
    return `OT${period - 4}`;
  };

  const getTeamName = (name) => {
    if (!name) return '---';
    if (nameMode === 'abbr') {
      return name.replace(/^(The |Team )/i, '').substring(0, 3).toUpperCase();
    }
    return name.length > 14 ? name.substring(0, 14) : name;
  };

  if (!game) return null;

  const homeScore = game.score?.home || 0;
  const awayScore = game.score?.away || 0;
  const period = game.clock?.period || 1;
  const clockMs = game.clock?.time_remaining_ms;
  const isRunning = game.clock?.is_running;
  const isLive = game.status === 'live' || game.status === 'in_progress';
  const isFinal = game.status === 'final' || game.status === 'completed';
  const homeColor = game.home_team_color || '#3B82F6';
  const awayColor = game.away_team_color || '#EF4444';

  // Size config
  const sizeConfig = {
    sm: { text: 'text-xs', score: 'text-lg', clock: 'text-[10px]', bar: 'w-1', pad: 'px-2 py-0.5', gap: 'min-w-[120px]' },
    md: { text: 'text-sm', score: 'text-2xl', clock: 'text-xs', bar: 'w-1.5', pad: 'px-3 py-1', gap: 'min-w-[180px]' },
    lg: { text: 'text-base', score: 'text-3xl', clock: 'text-sm', bar: 'w-2', pad: 'px-4 py-1.5', gap: 'min-w-[240px]' },
  };
  const s = sizeConfig[size] || sizeConfig.md;

  // Position config
  const posMap = {
    'top-left': 'top-4 left-4',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
  };
  const posClass = posMap[position] || posMap['top-center'];

  // Theme config
  const themes = {
    dark: {
      bg: 'bg-black/80',
      border: 'border border-white/10',
      text: 'text-white',
      textMuted: 'text-white/70',
      separator: 'border-white/10',
      clockBg: 'bg-black/60',
      statusBg: 'bg-red-600',
    },
    light: {
      bg: 'bg-white/90',
      border: 'border border-black/10',
      text: 'text-gray-900',
      textMuted: 'text-gray-500',
      separator: 'border-black/10',
      clockBg: 'bg-white/80',
      statusBg: 'bg-red-600',
    },
    glass: {
      bg: 'bg-white/10 backdrop-blur-xl',
      border: 'border border-white/20',
      text: 'text-white',
      textMuted: 'text-white/60',
      separator: 'border-white/15',
      clockBg: 'bg-white/10 backdrop-blur-lg',
      statusBg: 'bg-red-500/90',
    },
    minimal: {
      bg: 'bg-transparent',
      border: '',
      text: 'text-white',
      textMuted: 'text-white/60',
      separator: 'border-transparent',
      clockBg: 'bg-transparent',
      statusBg: 'bg-red-600',
    },
  };
  const t = themes[theme] || themes.dark;

  const flashClass = 'animate-score-flash';

  return (
    <div
      className="fixed inset-0 overflow-hidden pointer-events-none"
      style={{ background: 'transparent' }}
      data-testid="game-overlay-container"
    >
      <style>{`
        @keyframes score-flash {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.25); color: #FFD700; }
        }
        .animate-score-flash {
          animation: score-flash 0.6s ease-out;
        }
        @keyframes pulse-live {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .animate-pulse-live {
          animation: pulse-live 1.5s ease-in-out infinite;
        }
      `}</style>

      <div className={`absolute ${posClass}`} data-testid="game-overlay-scoreboard">
        <div className={`rounded-lg overflow-hidden shadow-2xl ${t.bg} ${t.border}`}>
          {/* Home team row */}
          <div className="flex items-center" data-testid="overlay-home-team">
            <div className={`${s.bar} self-stretch flex-shrink-0`} style={{ backgroundColor: homeColor }} />
            <div className={`flex items-center justify-between ${s.gap} ${s.pad}`}>
              <span className={`${s.text} ${t.text} font-semibold tracking-wide`}>
                {getTeamName(game.home_team_name)}
              </span>
              <span
                className={`${s.score} ${t.text} font-black tabular-nums ml-4 ${scoreFlash.home ? flashClass : ''}`}
                data-testid="overlay-home-score"
              >
                {homeScore}
              </span>
            </div>
          </div>

          {/* Separator */}
          <div className={`border-t ${t.separator}`} />

          {/* Away team row */}
          <div className="flex items-center" data-testid="overlay-away-team">
            <div className={`${s.bar} self-stretch flex-shrink-0`} style={{ backgroundColor: awayColor }} />
            <div className={`flex items-center justify-between ${s.gap} ${s.pad}`}>
              <span className={`${s.text} ${t.text} font-semibold tracking-wide`}>
                {getTeamName(game.away_team_name)}
              </span>
              <span
                className={`${s.score} ${t.text} font-black tabular-nums ml-4 ${scoreFlash.away ? flashClass : ''}`}
                data-testid="overlay-away-score"
              >
                {awayScore}
              </span>
            </div>
          </div>
        </div>

        {/* Clock / Status bar */}
        {(showClock || isLive || isFinal) && (
          <div className="flex justify-center mt-1" data-testid="overlay-status-bar">
            <div className={`rounded-full ${s.pad} flex items-center gap-2 ${t.clockBg} ${t.border}`}>
              {isLive && (
                <span className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse-live" />
                  <span className={`${s.clock} font-bold text-red-500`}>LIVE</span>
                </span>
              )}
              {isFinal && (
                <span className={`${s.clock} font-bold ${t.textMuted}`}>FINAL</span>
              )}
              {showClock && !isFinal && (
                <>
                  <span className={`${s.clock} ${t.textMuted} font-medium`}>
                    {getPeriodLabel(period, game.sport)}
                  </span>
                  <span className={`${s.clock} ${t.text} font-mono font-bold ${isRunning ? '' : 'opacity-70'}`}>
                    {formatTime(clockMs)}
                  </span>
                </>
              )}
              {!connected && (
                <span className={`${s.clock} text-yellow-400 font-medium`}>
                  reconnecting...
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
